import React, { useEffect } from 'react'
import styled from '@emotion/styled'
import { Helmet } from '@dr.pogodin/react-helmet'

import {
    Card,
    CardContent as MuiCardContent,
    Grid,
    Divider as MuiDivider,
    Typography as MuiTypography,
    useMediaQuery,
} from '@mui/material'
import { spacing, useTheme } from '@mui/system'

import Stats from './Stats'
import useFetchEntitiesWithCache from '../../../hooks/useFetchEntitiesWithCache'
import { formatNumber, formatPercentage } from '../../../packages/utils/numberFormatters'
import { useTranslation } from 'react-i18next'
import { categorizeIssues } from '../../../components/utils/categorizeIssues'
import { OngoingIssuesStats } from '../../statistics/issue-market/IssueMarketOngoingIssuesStats'
import { IssuesNews } from './IssuesNews'
import { hasSourceOfType } from '../../../components/utils/calculateIssueStatus'
import { categorizeWarrants } from '../../warrants/WarrantsView'
import { FELabel, HasWarrantLabel } from '../../../components/entities/IssueTypeLabels'
import { useQuery } from '../../../hooks/useQuery'
import { useNavigate } from 'react-router'
import {
    getCommitmentForLatestIssuesAtDate,
    getCommitmentExclGuarantorsForLatestIssuesAtDate,
} from '../../statistics/issue-market/utils'
import { getListedWarrantsForDates, ListedWarrantsChart } from './ListedWarrantsChart'

const CardContent = styled(MuiCardContent)`
    padding: 0px;

    &:last-child {
        padding-bottom: 0px;
    }
`

const Divider = styled(MuiDivider)(spacing)

const Typography = styled(MuiTypography)(spacing)

const chartTypes = [
    {
        id: 'ongoing-issues',
        label: 'Antal kommande och pågående företrädesemissioner',
    },
    {
        id: 'commitment',
        label: 'Genomsnittlig säkerställandegrad de senaste 20 emissionerna',
    },
    {
        id: 'commitment-excl-guarantors',
        label: 'Genomsnittligt utfall de senaste 20 emissionerna',
    },
    {
        id: 'listed-warrants',
        label: 'Antal noterade teckningsoptioner',
    },
] as const

type ChartType = (typeof chartTypes)[number]['id']

function Default() {
    const {
        i18n: { language: locale },
    } = useTranslation()
    const query = useQuery()
    const navigate = useNavigate()
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
    const { isLoading, result: issues } = useFetchEntitiesWithCache('issue')
    const chartRoutingParam = 'chartType'
    const defaultChart: ChartType = chartTypes[0].id
    const selectedChart: ChartType = chartTypes.find((i) => i.id === query.get(chartRoutingParam))?.id || defaultChart

    useEffect(() => {
        if (!query.get(chartRoutingParam)) {
            query.set(chartRoutingParam, defaultChart)
        }

        const chartType = chartTypes.find((i) => i.id === query.get(chartRoutingParam))

        if (!chartType) {
            // Reset to default if there's an unknown chart type in query param
            query.set(chartRoutingParam, defaultChart)
        }

        navigate(`?${query.toString()}`, { replace: true })
    }, [query, navigate, defaultChart, chartRoutingParam])

    const filteredIssues =
        issues?.filter((i) => !hasSourceOfType(issues, 'Cancellation') && i.type === 'Rights issue') || []
    const { currentIssues, futureIssues } = categorizeIssues(filteredIssues)
    const ongoingAndUpcomingIssues = [...currentIssues, ...futureIssues].filter((i) => !!i)
    const [commitmentExclGuarantors] = getCommitmentExclGuarantorsForLatestIssuesAtDate(filteredIssues, [new Date()])
    const [averageCommitment] = getCommitmentForLatestIssuesAtDate(filteredIssues, [new Date()])

    const { isLoading: isLoadingWarrants, result: warrants } = useFetchEntitiesWithCache('warrant')
    const { currentWarrants, futureWarrants } = categorizeWarrants(warrants || [])
    const [listedWarrants] = getListedWarrantsForDates([...currentWarrants, ...futureWarrants], [new Date()])

    const handleClickStats = (graphType: ChartType) => {
        query.set(chartRoutingParam, graphType)
        navigate('?' + query.toString())
    }

    return (
        <React.Fragment>
            <Helmet title='Överblick' />
            <Grid
                container
                spacing={6}
                sx={{
                    justifyContent: 'space-between',
                    mb: 6,
                }}
            >
                <Grid>
                    <Typography variant='h3'>Överblick</Typography>
                </Grid>
            </Grid>
            <Divider my={6} />
            <Grid container spacing={4}>
                <Grid
                    size={{ xs: 12, xl: 5 }}
                    sx={{
                        alignItems: 'stretch',
                    }}
                >
                    <Grid container spacing={4} sx={{ height: '100%' }}>
                        <Grid size={{ xs: 6 }}>
                            <Stats
                                title='Antal emissioner'
                                amount={`${formatNumber(ongoingAndUpcomingIssues?.length, { decimals: 0, locale })} st`}
                                description='Pågående och kommande'
                                isSlim={isSmallScreen}
                                isLoading={isLoading}
                                isSelected={selectedChart === 'ongoing-issues'}
                                label={<FELabel />}
                                onClick={() => handleClickStats('ongoing-issues')}
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <Stats
                                title='Antal noterade optioner'
                                amount={`${formatNumber(listedWarrants, { decimals: 0, locale })} st`}
                                description='Pågående och kommande teckningsoptioner'
                                isSlim={isSmallScreen}
                                isLoading={isLoadingWarrants}
                                isSelected={selectedChart === 'listed-warrants'}
                                label={<HasWarrantLabel />}
                                onClick={() => handleClickStats('listed-warrants')}
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <Stats
                                title='Teckningsgrad'
                                amount={`${formatPercentage(commitmentExclGuarantors, { decimals: 1, locale })}`}
                                description='Senaste 20 företrädesemissionerna'
                                isSlim={isSmallScreen}
                                isLoading={isLoading}
                                isSelected={selectedChart === 'commitment-excl-guarantors'}
                                label={<FELabel />}
                                onClick={() => handleClickStats('commitment-excl-guarantors')}
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <Stats
                                title='Säkerställandegrad'
                                amount={`${formatPercentage(averageCommitment, { decimals: 1, locale })}`}
                                description='Senaste 20 företrädesemissionerna'
                                isSlim={isSmallScreen}
                                isLoading={isLoading}
                                isSelected={selectedChart === 'commitment'}
                                label={<FELabel />}
                                onClick={() => handleClickStats('commitment')}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid size={{ xs: 12, xl: 7 }}>
                    <Card>
                        <CardContent>
                            <Typography variant='h6' sx={{ padding: '16px' }}>
                                {chartTypes.find(({ id }) => id === selectedChart)?.label}
                            </Typography>
                            {selectedChart === 'listed-warrants' ? (
                                <ListedWarrantsChart daysCount={90} isLoading={isLoadingWarrants} warrants={warrants} />
                            ) : (
                                <OngoingIssuesStats
                                    issues={filteredIssues}
                                    hasComparison
                                    daysCount={90}
                                    isLoading={isLoading}
                                    metricType={selectedChart}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={12}>
                    <IssuesNews isSlim={isSmallScreen} />
                </Grid>
            </Grid>
        </React.Fragment>
    )
}

export default Default
